import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  DialogContent,
  Dialog,
  Button,
  DialogActions,
  CircularProgress,
  Switch,
  DialogTitle,
  Fab,
  Chip,
  Collapse,
} from "@mui/material";
import { GapJustificationDialog } from "./GapJustificationDialog";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import Controls from "../../../atoms/Controls";
import {
  MoreVert,
  Cancel,
  CheckCircle,
  Note,
  NoteAlt,
  ManageHistory,
  Edit,
  Add,
  Today,
  Check,
  CircleOutlined,
  Layers,
  ExpandMore,
  ExpandLess,
  RecordVoiceOver,
  Print,
} from "@mui/icons-material";
import notificationStore from "../../../stores/Notification/notificationStore";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import {
  AppointmentDto,
  ContinuousAppointmentDto,
  PatientDto,
  TimeSlotDto,
  GapJustificationDto,
} from "../../../api-client";
import useStore from "../../../helpers/useStore";
import AppointmentNotes from "../../../molecules/Calendar/AppointmentNotes";
import { CancelAppointmentDialog } from "../../../molecules/CancelAppointmentDialog";
import { CopyAppointmentDialog } from "../../../molecules/CopyAppointmentDialog";
import TicketForm from "../../Ticketing/TicketEditCreate/ticketForm";
import ConfirmationDialog from "./RxSeries/ConfirmationDialog";
import DialogBoxAppointment from "../../PrintAppointment/printRxDialogBox";
import { TerminViewConstants } from "../TerminView";
import { TherapySlotRow } from "../../../molecules/TherapySlotRow";
import { cardShell } from "../../../molecules/cardShell";
import { GapIndicator } from "../../../molecules/GapIndicator";
import { AppointmentTime } from "../../../molecules/AppointmentTime";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import AppointmentEditCard from "../../ScheduleOverview/Appointments/AppointmentEditCard";
import { slotTherapy, slotsInChronologicalOrder } from "../../../helpers/slotTherapy";

import WarningDialog from "../../../atoms/WarningDialog";
import { SetInformedDialog, InformedDialogSlot } from "../../PatientInforming/SetInformedDialog";
import AppointmentOverviewModal from "./AppointmentOverviewModal";
import moment from "moment";
import AppointmentScheduler from "../../ScheduleOverview/AppointmentScheduler/AppointmentScheduler";
import { useNavigate } from "react-router-dom";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { CalendarCommands } from "../../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { useApi } from "../../../api/ApiProvider";
import {
  useAttendTimeSlot,
  useCancellationOfCancelledAppointment,
  useCopyNewAppointment,
  useSetAttended,
  useBackfillContinuousAppointment,
  useCancelContinuous,
} from "../../../stores/Appointment/appointment.mutation";
import { useAppointmentNotes } from "../../../stores/Notes/notes.queries";
import CreateTicketForm from "../../Ticketing/TicketEditCreate/createTicketForm";
import EditCancellationDialog from "../../ScheduleOverview/Appointments/CancelAppointmentEditCard";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import {
  groupAppointmentsBySignature,
  AppointmentGroup,
} from "../../../helpers/appointmentGrouping";

interface TerminViewProps {
  appointments: AppointmentDto[] | ContinuousAppointmentDto[];
  patientId: any;
  appointmentsLoading?: boolean;
  isView: TerminViewConstants;
  isContinuous?: boolean;
  setUnScheduledSelectedAppointmentId?: any;
  setDeleteEvent?: any;
  includeAllAppointments?: boolean;
  isSelectionMode?: boolean;
  selectedAppointments?: string[];
  setSelectedAppointments?: React.Dispatch<React.SetStateAction<string[]>>;
  inActiveCount?: number | null;
}

/**
 * Menu trigger on a dense row. MUI's IconButton ships 8px of padding around a
 * 24px glyph, so a stock kebab is 40px tall — on the date/time line that made
 * the button, not the text, set the row height, and the surplus was space for
 * the row below to overlap into.
 */
const denseMenuButtonSx = {
  padding: 0,
  margin: 0,
  // Square, exactly as tall as the header's text line box, and top-aligned so
  // its top edge meets the top of the time beside it. Sizing the BOX rather
  // than the glyph is what keeps the button from setting the row height again:
  // the height is derived from the same token the text uses, so it follows the
  // text instead of leading it.
  alignSelf: "flex-start",
  flexShrink: 0,
  height: "calc(var(--text-lead) * 1.3)",
  width: "calc(var(--text-lead) * 1.3)",
  "& .MuiSvgIcon-root": { width: "100%", height: "100%" },
};

// Function to format the gap between appointments
export const formatGap = (endDate: Date, nextStartDate: Date) => {
  if (endDate && nextStartDate) {
    // Strip time from dates
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const nextStartDateOnly = new Date(
      nextStartDate.getFullYear(),
      nextStartDate.getMonth(),
      nextStartDate.getDate()
    );

    const diffTime = Math.abs(nextStartDateOnly.getTime() - endDateOnly.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } else {
    return null; // or return some default value or handle error appropriately
  }
};

// Patient name for the "Patient informieren" dialog header, derived from the
// appointment's first time slot (the patient is the same across slots).
const getAppointmentPatientName = (appointment: any): string | undefined => {
  const patient = appointment?.timeSlots?.[0]?.therapyRx?.rx?.patient;
  if (!patient) return undefined;
  return `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim() || undefined;
};

// Every time slot of the appointment for the dialog: its times, the rx number
// and therapy abbreviation it delivers, and its own informing projection.
const getAppointmentSlots = (appointment: any): InformedDialogSlot[] =>
  (appointment?.timeSlots ?? []).map((ts: any) => ({
    id: ts?.id,
    start: ts?.start,
    end: ts?.end,
    informing: ts?.informing,
    rxNumber: ts?.therapyRx?.rx?.rxNumber,
    therapy: slotTherapy(ts)?.abbreviation,
  }));

export const AppointmentCardView = observer(
  ({
    appointment,
    appointments,
    patientId,
    gapInDays,
    isContinous = false,
    isView,
    setUnScheduledSelectedAppointmentId,
    setDeleteEvent,
    appointmentIndex,
  }: {
    appointment: any;
    appointments: any;
    patientId: any;
    gapInDays?: number | null;
    isContinous?: boolean;
    isView?: TerminViewConstants; //This is made optional for the PatientAdress component
    setUnScheduledSelectedAppointmentId?: any;
    setDeleteEvent?: any;
    appointmentIndex?: number;
  }) => {
    const { timeSlots } = appointment;
    const { mutate: copyAppointment } = useCopyNewAppointment();
    const [informedDialogOpen, setInformedDialogOpen] = useState(false);
    const [showNotesDialog, setShowNotesDialog] = useState(false);
    const [gapDialogOpen, setGapDialogOpen] = useState(false);

    const initialGapJustificationData: GapJustificationDto = {
      description: "",

      patientId: appointment.timeSlots?.[0]?.therapyRx?.rx?.patient?.id || patientId,
      startDate:
        appointments && appointmentIndex !== undefined && appointments[appointmentIndex]?.start
          ? new Date(appointments[appointmentIndex]?.start).toISOString()
          : null,
      endDate:
        appointments && appointmentIndex !== undefined && appointments[appointmentIndex + 1]?.end
          ? new Date(appointments[appointmentIndex + 1]?.end).toISOString()
          : null,
    };

    const hasGapJustification = !!appointment?.gapJustifications?.length;

    const gapJustificationData: GapJustificationDto[] = useMemo(
      () =>
        appointment?.gapJustifications?.length
          ? appointment.gapJustifications
          : [initialGapJustificationData],
      [appointment?.gapJustifications, initialGapJustificationData],
    );


    const handleOpenNotesDialog = () => {
      setShowNotesDialog(true);
    };

    const handleCloseNotesDialog = () => {
      setShowNotesDialog(false);
    };

    const [openTicket, setOpenTicket] = useState<any>(false);
    const [ticketData, setTicketData] = useState<any>(null);

    const [isCancellationDialogOpen, setCancellationDialogOpen] = useState(false);
    const [isCopyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyIsContinuous, setCopyIsContinuous] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDto | null>(null); // Track the selected appointment for the dialog
    const [isSuggestionDialogOpen, setsuggestionDialogOpen] = useState(false);
    const handleCloseCancellation = (actionPerformed: boolean) => {
      setCancellationDialogOpen(false);
      // if (actionPerformed && setDataRefreshFlag) setDataRefreshFlag(new Date().getTime());
    };

    const handleOpenCancellationDialog = (appointmentId: any) => {
      // Create an object with appointmentId and appointmentStart
      const selectedAppointment = appointments.find((app: any) => app.id === appointmentId);
      if (!selectedAppointment) {
        console.error("Appointment not found for id:", appointmentId);
        // use the snackbar to show an error message
        notificationStore.showMessage("Termin nicht gefunden: " + appointmentId, "error");
        return;
      }
      setSelectedAppointment(selectedAppointment);
      setCancellationDialogOpen(true);
    };
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false); // State for the edit dialog

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    const handleEditDialogOpen = () => {
      setEditDialogOpen(true);
      handleMenuClose();
    };

    const handleEditDialogClose = () => {
      setEditDialogOpen(false);
      handleMenuClose();
    };

    const handleOpenInformedDialog = () => {
      setInformedDialogOpen(true);
      handleMenuClose();
    };

    // const [menu, setMenu] = useState<{ [key: string]: HTMLElement | null }>({});

    // const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, appointmentId: any) => {
    //   setMenu({ ...menu, [appointmentId]: event.currentTarget });
    // };

    // const handleMenuClose = (appointmentId: any) => {
    //   setMenu({ ...menu, [appointmentId]: null });
    //   const handleOpenCancellationDialog = (appointmentId: any) => {
    //     // Create an object with appointmentId and appointmentStart
    //     const selectedAppointment = appointments.find((app: any) => app.id === appointmentId);
    //     if (!selectedAppointment) {
    //       console.error("Appointment not found for id:", appointmentId);
    //       // use the snackbar to show an error message
    //       notificationStore.showMessage("Termin nicht gefunden: " + appointmentId, "error");
    //       return;
    //     }
    //     setSelectedAppointment(selectedAppointment);
    //     setCancellationDialogOpen(true);
    //   };

    //   const handleAttended = async (appointmentId: any, isAttended: boolean) => {
    //     try {
    //       // Call your API or setAttended function with the appointmentId and isAttended value
    //       await setAttended(appointmentId, isAttended);
    //       setDataRefreshFlag(isAttended);
    //     } catch (error) {
    //       console.error("Error marking as attended/unattended:", error);
    //     }
    //   };
    // };

    const openTicketForm = (appointment: any) => {
      // Iterate through each appointment
      // appointments.forEach((appointment) => {

      // Initialize an array to store RX IDs and associated user and therapy data
      const rxData: any[] = [];
      let patient: any;

      // Iterate through each time slot in the appointment
      appointment.timeSlots.forEach((timeSlot: any) => {
        // Extract the patient ID from the first digit of the RX number
        const patientId =
          timeSlot?.therapyRx?.rx?.rxNumber?.split("-")[0] || timeSlot.therapyRx.rx.patient.id;

        // Extract the RX ID, user data, and therapy ID from the time slot
        const rxNumber = timeSlot.therapyRx?.rx.rxNumber!;
        const user = timeSlot.user;
        const therapyId = slotTherapy(timeSlot);

        // Extract patient data
        patient = {
          patientId: patientId,
          patientData: timeSlot.therapyRx.rx.patient,
        };

        // Construct the data object for each time slot and push it to rxData array
        rxData.push({
          therapyRx: {
            rx: {
              rxNumber: rxNumber,
            },
            therapy: therapyId,
          },
          user: user,
        });
      });

      // Assuming `patient` is defined elsewhere
      setTicketData({
        appointmentId: appointment.id,
        appointment: { timeSlots: rxData },
        patientId: patient.patientId,
        patient: patient.patientData,
      });

      // });
      setOpenTicket(!openTicket);
    };
    const handleCloseTicketForm = () => {
      setOpenTicket(false);
    };

    const currentDate: any = new Date();
    const handleCopy = (appointmentId: string, isContinuous: boolean) => {
      if (!appointmentId) {
        notificationStore.showMessage(
          "No appointment ID found for copying appointment:",
          "error"
        );
        return;
      }
      // With a single timeslot there is nothing to choose, so skip the dialog and copy directly.
      if ((appointment.timeSlots?.length ?? 0) <= 1) {
        const timeSlotIds = appointment.timeSlots
          ?.map((slot: any) => slot.id)
          .filter(Boolean);
        copyAppointment({ appointmentId, isContinuous, patientId, timeSlotIds });
        return;
      }
      // Otherwise open the timeslot selection dialog; the actual copy is triggered on confirm.
      setCopyIsContinuous(isContinuous);
      setCopyDialogOpen(true);
    };

    const handleCloseCopyDialog = (_actionPerformed: boolean) => {
      setCopyDialogOpen(false);
    };

    // Clicking the card is the same navigation as the menu's "zum Kalender" —
    // the obvious thing to do with an appointment you can see, without going
    // via the menu for it. The menu keeps the item: it is the keyboard route,
    // and removing it would make this action mouse-only.
    const cardNavigate = useNavigate();
    const cardCalendarStore = useStore(CalendarContext);
    const { navigateToCalendar: navigateCardToCalendar } =
      CalendarCommands(cardCalendarStore);
    const jumpToCalendar = (event: React.MouseEvent<HTMLElement>) => {
      if (!appointment?.start) return;
      // Menus and dialogs render into portals, but React bubbles their events
      // up the REACT tree — so a click inside an open Stornieren dialog would
      // otherwise reach this handler and navigate out from under the user.
      // Portalled content is never a DOM descendant of the card, so containment
      // is the reliable test, and it covers every portal at once rather than
      // needing a stopPropagation on each new one.
      if (!event.currentTarget.contains(event.target as Node)) return;

      const patientData = appointment?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto;
      navigateCardToCalendar(cardNavigate, {
        patientId,
        patientLastName: patientData?.lastName,
        date: moment(appointment.start).format("YYYY-MM-DD"),
        appointmentId: appointment.id,
      });
    };

    return (
      <Box sx={{ margin: "0px", padding: "0px", border: "0px dotted red" }}>
        {/* Icon Button for adding notes as a tag */}

        <Box
          className={cardShell()}
          onClick={jumpToCalendar}
          sx={{
            position: "relative",
            p: 0,
            m: 0,
            padding: "2px 4px",
            margin: "0px",
            cursor: appointment?.start ? "pointer" : "default",
          }}
        >
          {appointment.notes && appointment.notes.length > 0 && (
            <Badge
              color="secondary"
              sx={{
                position: "absolute",
                top: "0%",
                left: "-3%",
                transform: "translate(-50%, -50%)",
                cursor: "pointer",
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenNotesDialog();
              }}
            >
              <NoteAlt />
            </Badge>
          )}
          {/* First grid in one row */}
          <Grid container>
            <Grid item xs={6} display="flex" justifyContent="space-between">
              {/* Date on the left side */}
              <span
                className="text-lead font-bold tabular-nums text-ink-text"
                style={
                  appointment.end === null && appointment.originalDate
                    ? { textDecoration: "line-through", textDecorationThickness: "2px" }
                    : undefined
                }
              >
                {appointment.start && !appointment.unschedule
                  ? isView === TerminViewConstants.CONTINOUS_TERMIN
                    ? moment(appointment.start).format("dddd")
                    : formatDateUI(appointment.start)
                  : ""}
              </span>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              {/* Start time on the right side; the end lives in the tooltip. */}
              {!appointment.unschedule &&
                (isView === TerminViewConstants.RX_VIEW &&
                appointment.start &&
                appointment.end &&
                appointment.timeSlots?.length > 0 ? (
                  // This Rx's slots only, so the appointment may start earlier
                  // than the treatment shown — that is the parenthesised time.
                  <AppointmentTime
                    className="text-lead font-bold tabular-nums text-ink-text"
                    start={appointment.timeSlots[0].start}
                    end={appointment.timeSlots[appointment.timeSlots.length - 1].end}
                    appointmentStart={appointment.start}
                    appointmentEnd={appointment.end}
                  />
                ) : (
                  <AppointmentTime
                    className="text-lead font-bold tabular-nums text-ink-text"
                    start={appointment.start}
                    end={appointment.end}
                    label="Termin"
                  />
                ))}

              {/* Menus live inside the clickable card, so they swallow their own
                  clicks rather than also jumping to the calendar. */}
              <Box onClick={(event) => event.stopPropagation()} sx={{ borderRadius: "8px", background: "transparent" }}>
                {isContinous && (
                  <ContinuousAppointmentMenu
                    appointment={appointment}
                    handleOpenCancellationDialog={handleOpenCancellationDialog}
                    openTicketForm={openTicketForm}
                    patientId={patientId}
                    handleCopy={handleCopy}
                    onAddNotes={handleOpenNotesDialog}
                  />
                )}
                {!isContinous &&
                  (isView === TerminViewConstants.TERMIN_VIEW ||
                    (isView === TerminViewConstants.RX_VIEW && appointment.end)) && (
                    <AppointmentMenu
                      appointment={appointment}
                      handleOpenCancellationDialog={handleOpenCancellationDialog}
                      openTicketForm={openTicketForm}
                      patientId={patientId}
                      handleCopy={handleCopy}
                      onAddNotes={handleOpenNotesDialog}
                      isView={isView}
                    />
                  )}
              </Box>
              <Box onClick={(event) => event.stopPropagation()}>
                {isView === TerminViewConstants.CANCELLED_TERMIN && (
                  <CancelledAppointmentMenu
                    appointment={appointment}
                    openTicketForm={openTicketForm}
                    patientId={patientId}
                  />
                )}
              </Box>
              <Box onClick={(event) => event.stopPropagation()}>
                {/* This is for unscheduled appointments */}
                {(isView === TerminViewConstants.UNSCHEDULED_TERMIN ||
                  isView === TerminViewConstants.UNSCHEDULED_CONT_TERMIN) && (
                  <>
                    <IconButton
                      sx={denseMenuButtonSx}
                      aria-label="more"
                      aria-controls={`menu-${appointment.id}`}
                      aria-haspopup="true"
                      onClick={(event) => handleMenuOpen(event)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      id={`menu-${appointment.id}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleEditDialogOpen}>
                        <Edit />
                        Bearbeiten
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setUnScheduledSelectedAppointmentId(appointment.id as string);
                          setDeleteEvent({
                            selectedAppointment: appointment.id as string,
                            isContinuous:
                              isView === TerminViewConstants.UNSCHEDULED_CONT_TERMIN ? true : false,
                            isDeleteDialogOpen: true,
                          });
                          // Close the kebab immediately — otherwise it stays open
                          // (and looks frozen) for the whole delete + refetch cycle.
                          handleMenuClose();
                        }}
                      >
                        <Tooltip title="löschen">
                          <>
                            <Cancel className="cancel-icon" />
                            löschen
                          </>
                        </Tooltip>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCopy(
                            appointment.id!,
                            isView === TerminViewConstants.UNSCHEDULED_TERMIN ? false : true
                          ); // flag set to true for continuous appointments
                          handleMenuClose();
                        }}
                      >
                        <Add /> Neuer Termin
                      </MenuItem>
                      {isView !== TerminViewConstants.UNSCHEDULED_CONT_TERMIN && (
                        <MenuItem
                          onClick={() => {
                            handleOpenNotesDialog();
                            handleMenuClose();
                          }}
                        >
                          <Edit />
                          Notizen hinzufügen/bearbeiten
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
                {isView === TerminViewConstants.SERIES_VIEW && appointment.end && (
                  <>
                    <IconButton
                      sx={denseMenuButtonSx}
                      aria-label="more"
                      aria-controls={`menu-${appointment.id}`}
                      aria-haspopup="true"
                      onClick={(event) => handleMenuOpen(event)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      id={`menu-${appointment.id}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleEditDialogOpen}>
                        <Edit />
                        Bearbeiten
                      </MenuItem>
                      <MenuItem onClick={handleOpenInformedDialog}>
                        <RecordVoiceOver />
                        Patient informieren
                      </MenuItem>
                    </Menu>
                  </>
                )}
                {/* This is for PatientAdress apointment view*/}
                {isView !== 0 && !isView && (
                  <>
                    <IconButton
                      sx={denseMenuButtonSx}
                      aria-label="more"
                      aria-controls={`menu-${appointment.id}`}
                      aria-haspopup="true"
                      onClick={(event) => handleMenuOpen(event)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      id={`menu-${appointment.id}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleEditDialogOpen}>
                        <Edit />
                        Bearbeiten
                      </MenuItem>
                      <MenuItem onClick={handleOpenInformedDialog}>
                        <RecordVoiceOver />
                        Patient informieren
                      </MenuItem>
                    </Menu>
                  </>
                )}
                {editDialogOpen && (
                  <AppointmentEditCard
                    open={editDialogOpen}
                    onClose={handleEditDialogClose}
                    appointment={appointment}
                    patientId={patientId}
                    isContinous={
                      isView === TerminViewConstants.UNSCHEDULED_CONT_TERMIN ? true : false
                    }
                    isView={isView}
                  />
                )}
              </Box>
            </Grid>

            {/* Render the WarningDialog conditionally */}
            {selectedAppointment && isCancellationDialogOpen && (
              <CancelAppointmentDialog
                patientId={patientId}
                isOpen={isCancellationDialogOpen}
                onClose={handleCloseCancellation}
                appointment={selectedAppointment}
                isView={isView}
              />
            )}

            {isCopyDialogOpen && (
              <CopyAppointmentDialog
                patientId={patientId}
                isOpen={isCopyDialogOpen}
                onClose={handleCloseCopyDialog}
                appointment={appointment}
                isContinuous={copyIsContinuous}
              />
            )}

            {openTicket && (
              <CreateTicketForm
                openTicket={openTicket}
                onCloseTicketForm={handleCloseTicketForm}
                data={ticketData}
              />
            )}

            <SetInformedDialog
              open={informedDialogOpen}
              onClose={() => setInformedDialogOpen(false)}
              appointmentId={appointment?.id}
              appointmentStart={appointment?.start ? new Date(appointment.start) : undefined}
              projection={appointment?.informing}
              visitType={appointment?.address?.type}
              patientName={getAppointmentPatientName(appointment)}
              slots={getAppointmentSlots(appointment)}
            />
          </Grid>

          {/* Second grid in another row */}
          <Grid container>
            {slotsInChronologicalOrder(timeSlots)
              .map((timeSlot: any, index: any) => (
                <Grid item xs={12} key={index}>
                  <TherapySlotRow
                    timeSlot={timeSlot}
                    appointment={appointment}
                    index={index}
                    totalSlots={timeSlots.length}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
        {(gapInDays || gapInDays === 0) && isView !== TerminViewConstants.CONTINOUS_TERMIN && (
          <>
            <GapIndicator
              gapInDays={gapInDays}
              hasJustification={hasGapJustification}
              onClick={
                isView === TerminViewConstants.RX_VIEW ||
                isView === TerminViewConstants.TERMIN_VIEW
                  ? () => setGapDialogOpen(true)
                  : undefined
              }
            />
            {(isView === TerminViewConstants.RX_VIEW || isView === TerminViewConstants.TERMIN_VIEW) && (
              <GapJustificationDialog
                open={gapDialogOpen}
                onClose={() => setGapDialogOpen(false)}
                gapInDays={gapInDays}
                gapJustificationData={gapJustificationData}
                rxData={appointment.timeSlots?.[0]?.therapyRx.rx}
                initialGapJustificationData={initialGapJustificationData}
               
              />
            )}
          </>
        )}
        {showNotesDialog && appointment.id && (
          <AppointmentNotes
            appointmentId={appointment.id}
            showDialog={showNotesDialog}
            onClose={handleCloseNotesDialog}
          />
        )}
      </Box>
    );
  }
);

const TerminView: React.FC<TerminViewProps> = observer(
  ({
    appointments,
    patientId,
    appointmentsLoading,
    isView,
    isContinuous = false,
    setUnScheduledSelectedAppointmentId,
    setDeleteEvent,
    includeAllAppointments,
    isSelectionMode = false, // new prop to determine selection mode
    selectedAppointments, // selected appointments passed as a prop
    setSelectedAppointments,
    inActiveCount,
  }) => {
  
    const [includeCancelled, setIncludeCancelled] = useState(includeAllAppointments ?? false);

    const AppointmentStore = useStore(AppointmentContext);
    const [isSwapLoading,setIsSwapLoading] = useState(false);
    // const { appointments: appointmentApi } = useApi();
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [showDialogBoxAppointment, setShowDialogBoxAppointment] = useState<boolean>(false); // for Termine ausdrucken button
    const [overviewModalState, setOverviewModalState] = useState({
      open: false,
      noFilters: false,
    });
    const [showAppointmentSchedulerModal, setShowAppointmentSchedulerModal] =
      useState<boolean>(false);
    const [selectAll, setSelectAll] = useState(false); // state to track "Select All" checkbox

    // Stacking: track which groups are expanded
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const toggleGroup = (key: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };
    // Keep selectAll in sync with selectedAppointments and appointments
    useEffect(() => {
      if (
        selectedAppointments &&
        appointments &&
        selectedAppointments.length === appointments.length &&
        appointments.length > 0
      ) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
    }, [selectedAppointments, appointments]);

    const navigate = useNavigate();
    const patientsStore = useStore(PatientsContext);
    const {setPreflightDialogOpen}=patientsStore;
    const calendarStore = useStore(CalendarContext);
    const { navigateToPatient } = PatientCommands(patientsStore);
    const { navigateToCalendar } = CalendarCommands(calendarStore);

    // Function to handle checkbox selection
    const handleCheckboxChange = (appointmentId: string) => {
      if (selectedAppointments && setSelectedAppointments) {
        if (selectedAppointments.includes(appointmentId)) {
          // If already selected, remove it
          setSelectedAppointments(selectedAppointments.filter((id) => id !== appointmentId));
        } else {
          // Otherwise, add to selected appointments
          setSelectedAppointments([...selectedAppointments, appointmentId]);
        }
      }
    };

    // Function to handle "Select All" checkbox change
    const handleSelectAllChange = () => {
      if (selectedAppointments && setSelectedAppointments) {
        if (selectAll) {
          // Deselect all appointments if "Select All" is unchecked
          setSelectedAppointments([]);
        } else {
          // Select all appointments
          setSelectedAppointments(appointments?.map((appointment: any) => appointment.id));
        }
        setSelectAll(!selectAll);
      }
    };

    const getValidStartDate = (appointment: any, index: number): Date => {
      if (index >= filteredAppointments.length - 1) {
        return appointment.start; // Return the start date of the appointment if it's the last one
      }
      return !appointment.end
        ? getValidStartDate(filteredAppointments[index + 1], index + 1)
        : appointment.start;
    };

    // const filteredAppointments = includeCancelled
    // ? appointments.filter((appointment: any) => appointment.originalDate || appointment.end)
    // : (isView === TerminViewConstants.SERIES_VIEW ||
    //     isView === TerminViewConstants.UNSCHEDULED_TERMIN ||
    //     isView === TerminViewConstants.UNSCHEDULED_CONT_TERMIN
    //   ? appointments
    //   : appointments.filter((appointment: any) => appointment.end));
    const filteredAppointments =
      includeCancelled ||
      isView === TerminViewConstants.SERIES_VIEW ||
      isView === TerminViewConstants.UNSCHEDULED_TERMIN ||
      isView === TerminViewConstants.UNSCHEDULED_CONT_TERMIN ||
      isView === TerminViewConstants.CANCELLED_TERMIN ||
      isView === TerminViewConstants.ALL_APPOINTMENTS
        ?appointments
        : appointments.filter((appointment: any) => appointment.end);

    // const handleSwapAppointments = async () => {
    //   // if(canSwap.every((result:any) => !result.canSwap)){
    //   //   notificationStore.showMessage("Termine können nicht verschoben werden, da kein anderer Termin geplant ist.","error")
    //   //   return;
    //   // }
    //   setIsSwapLoading(true);
    //   let uniqueIds: any;
    //   uniqueIds = new Set(); // Using a Set to automatically handle uniqueness

    //   filteredAppointments.forEach((appointment) => {
    //     appointment?.timeSlots?.forEach((timeSlot: any) => {
    //       if (timeSlot.therapyRx && timeSlot.therapyRx.rx?.rxSeries && timeSlot.therapyRx.rx?.rxSeries.id) {
    //         uniqueIds.add(timeSlot.therapyRx.rx?.rxSeries.id);
    //       }
    //     });
    //   });
    //      if(Array.from(uniqueIds)?.length>0){
    //   setPreflightDialogOpen(true, Array.from(uniqueIds))}
    //   setIsSwapLoading(false);
    //   // setIsSwapLoading(true);

    //   // const response = await swapAppointments({ seriesIds: Array.from(uniqueIds) });
    //   // setIsSwapLoading(false);
    //   // if (response && response.length > 0) {
    //   //   if (response.some((result: any) => result.perpetualAppointments.length > 0)) {
    //   //     setOpenConfirmation(!openConfirmation);
    //   //   }
    //   // }
    // };

    const handleSwapAppointments = async () => {
      setIsSwapLoading(true);

      const uniqueIds = new Set<string>();

      filteredAppointments.forEach((appointment) => {
        appointment?.timeSlots?.forEach((timeSlot: any) => {
          if (timeSlot.therapyRx && timeSlot.therapyRx.rx?.rxSeries && timeSlot.therapyRx.rx?.rxSeries.id) {
            uniqueIds.add(timeSlot.therapyRx.rx?.rxSeries.id);
          }
        });
      });

      // Only open dialog if there are series IDs
      if (uniqueIds.size > 0) {
        // Wait for dialog to be ready before hiding spinner
        setPreflightDialogOpen(true, Array.from(uniqueIds), true);
      }

      setIsSwapLoading(false);
    };

    // show a dialog box for printing the appointments
    const handleShowAppointment = () => {
      setShowDialogBoxAppointment(true);
    };

    const handleShowOverviewAppointmentModal = () => {
      setOverviewModalState({ open: true, noFilters: false });
    };

    const handleShowOverviewwithInActive = () => {
      setOverviewModalState({ open: true, noFilters: true });
    };

    const handleCloseOverviewAppointment = () => {
      setOverviewModalState({ open: false, noFilters: false });
    };

    const handleShowAppointmentScheduler = () => {
      setShowAppointmentSchedulerModal(true);
    };
    const handleCloseAppointmentScheduler = () => {
      setShowAppointmentSchedulerModal(false);
    };

    const handleHideAppointment = () => {
      setShowDialogBoxAppointment(false);
    };

    const closeConfirmation = () => {
      setOpenConfirmation(false);
    };

    // useEffect(() => {
    //   const fetchData = async () => {
    //     if (isView === TerminViewConstants.SERIES_VIEW && appointments.length > 0) {
    //       try {
    //         let uniqueIds: any;
    //         uniqueIds = new Set(); // Using a Set to automatically handle uniqueness
    //         appointments.forEach((appointment) => {
    //           appointment?.timeSlots?.forEach((timeSlot: any) => {
    //             if (
    //               timeSlot.therapyRx &&
    //               timeSlot.therapyRx.rx?.rxSeries &&
    //               timeSlot.therapyRx.rx?.rxSeries.id
    //             ) {
    //               uniqueIds.add(timeSlot.therapyRx.rx?.rxSeries.id);
    //             }
    //           });
    //         });
    //         const results = await appointmentApi.canSwapAppointments(Array.from(uniqueIds));
    //         setCanSwap(results);
    //       } catch (error) {
    //         console.error("Error fetching data:", error);
    //         // Handle error (e.g., show error message)
    //       }
    //     }
    //   };

    //   fetchData();
    // }, [isView === TerminViewConstants.SERIES_VIEW, appointments]);

    return (
      <>
        <>
          {isView === TerminViewConstants.RX_VIEW && (
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <Typography sx={{ fontSize: "0.7em" }}>Storniert/Ungeplant</Typography>
              <Switch
                checked={includeCancelled || false}
                onChange={(e) => {
                  setIncludeCancelled(e.target.checked);
                }}
              />
            </div>
          )}

          {isView === TerminViewConstants.SERIES_VIEW && (
            <Button
              onClick={handleSwapAppointments}
              variant="contained"
              color="primary"
              disabled={appointments.length === 0 || isSwapLoading}
            >
              neu ordnen
            </Button>
          )}
          <ConfirmationDialog open={openConfirmation} onClose={closeConfirmation} />
        </>

        {/* }
    /> */}
        {
          <AppointmentOverviewModal
            open={overviewModalState.open}
            onClose={handleCloseOverviewAppointment}
            patientId={patientId}
            noFilters={overviewModalState.noFilters}
          />
        }
        <>
          {inActiveCount && inActiveCount > 0 && isView === TerminViewConstants.TERMIN_VIEW && (
            <Typography
              variant="body2"
              color="secondary"
              sx={{ cursor: "pointer", textDecoration: "underline", fontSize: "14px" }}
              onClick={handleShowOverviewwithInActive}
            >
              {inActiveCount} abgerechnete Termine
            </Typography>
          )}
          <Grid container justifyContent="flex-end">
            <Grid
              item
              xs={"auto"}
              alignSelf="flex-end"
              sx={{ padding: "2px", border: "0px red solid" }}
            >
              {/* The drawer is too narrow for three text buttons on one line:
                  printing collapses to an icon, the rest go size="small". */}
              {appointments.length > 0 && isView === TerminViewConstants.TERMIN_VIEW && (
                <Tooltip title="Drucken">
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{ marginRight: 1 }}
                    onClick={handleShowAppointment}
                  >
                    <Print fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* LANGUAGE */}
              {isView === TerminViewConstants.TERMIN_VIEW && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleShowOverviewAppointmentModal}
                  sx={{ marginRight: 1 }}
                >
                  Terminübersicht
                </Button>
              )}
              {isView === TerminViewConstants.TERMIN_VIEW && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleShowAppointmentScheduler}
                >
                  Planer
                </Button>
              )}
              {showAppointmentSchedulerModal && (
                <AppointmentScheduler
                  open={showAppointmentSchedulerModal}
                  onClose={handleCloseAppointmentScheduler}
                  patientId={patientId}
                />
              )}

              {
                <DialogBoxAppointment
                  id={patientId}
                  openDialog={showDialogBoxAppointment}
                  handleOnClose={handleHideAppointment}
                  single={false}
                  tarifId={null}
                  rxDate={null}
                />
              }
            </Grid>
          </Grid>

          {appointmentsLoading ? (
            <CircularProgress size={60} />
          ) : !appointmentsLoading && filteredAppointments?.length > 0 ? (
            <>
              {/* "Select All" checkbox */}
              {isSelectionMode && (
                <Grid item xs={12}>
                  <Controls.Checkbox
                    label={
                      <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                        Wählen Sie Alle Termine aus.({filteredAppointments?.length!})
                      </Typography>
                    }
                    value={selectAll}
                    onChange={handleSelectAllChange}
                    inputProps={{ "aria-label": "select all appointments" }}
                    style={{ padding: "0px", marginBottom: "5px" }}
                  />
                </Grid>
              )}

              {/* ── Stacked rendering for UNSCHEDULED view ── */}
              {(isView === TerminViewConstants.UNSCHEDULED_TERMIN) ? (
                (() => {
                  const groups = groupAppointmentsBySignature(filteredAppointments ?? []);
                  return groups.map((group, gIdx) => {
                    const groupKey = `${group.signature}-${gIdx}`;
                    const isExpanded = expandedGroups.has(groupKey);
                    const rep = group.representative;

                    return (
                      <React.Fragment key={groupKey}>
                        {/* ── Main group row ── */}
                        <Grid container sx={{ mb: 1 }}>
                          <Grid item xs={isSelectionMode ? 10 : 12}>
                            <Box sx={{ position: "relative" }}>
                              <AppointmentCardView
                                appointment={rep}
                                appointments={filteredAppointments}
                                patientId={patientId}
                                gapInDays={null}
                                isContinous={isContinuous}
                                isView={isView}
                                setUnScheduledSelectedAppointmentId={
                                  setUnScheduledSelectedAppointmentId
                                }
                                setDeleteEvent={setDeleteEvent}
                              />
                              {/* Stack badge */}
                              {group.count > 1 && (
                                <Chip
                                  icon={<Layers sx={{ fontSize: 16 }} />}
                                  label={`×${group.count}`}
                                  size="small"
                                  color="primary"
                                  onClick={() => toggleGroup(groupKey)}
                                  onDelete={() => toggleGroup(groupKey)}
                                  deleteIcon={
                                    isExpanded ? (
                                      <ExpandLess sx={{ fontSize: 16 }} />
                                    ) : (
                                      <ExpandMore sx={{ fontSize: 16 }} />
                                    )
                                  }
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    zIndex: 2,
                                  }}
                                />
                              )}
                            </Box>
                          </Grid>
                          {isSelectionMode && group.count <= 1 && (
                            <Grid item xs={2} display="flex" justifyContent="flex-end">
                              <Fab
                                color={
                                  selectedAppointments?.includes(rep.id!)
                                    ? "primary"
                                    : "default"
                                }
                                size="small"
                                onClick={() => handleCheckboxChange(rep.id!)}
                                sx={{ boxShadow: 3 }}
                              >
                                {selectedAppointments?.includes(rep.id!) ? (
                                  <Check />
                                ) : (
                                  <Add />
                                )}
                              </Fab>
                            </Grid>
                          )}
                        </Grid>

                        {/* ── Expanded sub-rows ── */}
                        {group.count > 1 && (
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                pl: 2,
                                borderLeft: "3px solid",
                                borderColor: "primary.main",
                                ml: 1,
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 0.5, display: "block" }}
                              >
                                {group.count} identische Termine ({group.label})
                              </Typography>
                              {group.appointments.map(
                                (subAppt: any, subIdx: number) => (
                                  <React.Fragment key={subAppt.id}>
                                    <Grid container sx={{ mb: 1 }}>
                                      <Grid
                                        item
                                        xs={isSelectionMode ? 10 : 12}
                                      >
                                        <AppointmentCardView
                                          appointment={subAppt}
                                          appointments={filteredAppointments}
                                          patientId={patientId}
                                          gapInDays={null}
                                          isContinous={isContinuous}
                                          isView={isView}
                                          setUnScheduledSelectedAppointmentId={
                                            setUnScheduledSelectedAppointmentId
                                          }
                                          setDeleteEvent={setDeleteEvent}
                                        />
                                      </Grid>
                                      {isSelectionMode && (
                                        <Grid
                                          item
                                          xs={2}
                                          display="flex"
                                          justifyContent="flex-end"
                                        >
                                          <Fab
                                            color={
                                              selectedAppointments?.includes(
                                                subAppt.id
                                              )
                                                ? "primary"
                                                : "default"
                                            }
                                            size="small"
                                            onClick={() =>
                                              handleCheckboxChange(subAppt.id)
                                            }
                                            sx={{ boxShadow: 3 }}
                                          >
                                            {selectedAppointments?.includes(
                                              subAppt.id
                                            ) ? (
                                              <Check />
                                            ) : (
                                              <Add />
                                            )}
                                          </Fab>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </React.Fragment>
                                )
                              )}
                            </Box>
                          </Collapse>
                        )}
                      </React.Fragment>
                    );
                  });
                })()
              ) : (
                /* ── Original flat rendering for all other views ── */
                filteredAppointments?.map((appointment: any, index: number) => {
                  return (
                    <React.Fragment key={appointment.id}>
                      <Grid
                        container
                        key={appointment.id}
                        sx={{ mb: 1 }}
                      >
                        <Grid item xs={isSelectionMode ? 10 : 12}>
                          <AppointmentCardView
                            appointment={appointment}
                            appointments={filteredAppointments}
                            patientId={patientId}
                            gapInDays={
                              filteredAppointments[index + 1] &&
                              filteredAppointments[index]?.end
                                ? formatGap(
                                    new Date(getValidStartDate(appointment, index)),
                                    new Date(
                                      getValidStartDate(
                                        filteredAppointments[index + 1],
                                        index + 1
                                      )
                                    )
                                  )
                                : null
                            }
                            isContinous={isContinuous}
                            isView={isView}
                            setUnScheduledSelectedAppointmentId={
                              setUnScheduledSelectedAppointmentId
                            }
                            setDeleteEvent={setDeleteEvent}
                            appointmentIndex={index}
                          />
                        </Grid>
                        {isSelectionMode && (
                          <Grid item xs={2} display="flex" justifyContent="flex-end">
                            <Fab
                              color={
                                selectedAppointments?.includes(appointment.id)
                                  ? "primary"
                                  : "default"
                              }
                              size="small"
                              onClick={() => handleCheckboxChange(appointment.id)}
                              sx={{ boxShadow: 3 }}
                            >
                              {selectedAppointments?.includes(appointment.id) ? (
                                <Check />
                              ) : (
                                <Add />
                              )}
                            </Fab>
                          </Grid>
                        )}
                      </Grid>
                    </React.Fragment>
                  );
                })
              )}
            </>
          ) : (
            <Typography variant="body1" component="p" sx={{}}>
              Keine Termine
            </Typography>
          )}
        </>
      </>
    );
  }
);

interface MenuInterface {
  appointment: AppointmentDto;
  handleOpenCancellationDialog: (appointmentId: any) => void;
  openTicketForm: (appointment: AppointmentDto) => void;
  patientId: number;
  handleCopy: (appointmentId: string, isContinous: boolean) => any;
  onAddNotes?: any;
  isView?: any;
}

interface CancelledMenuInterface {
  appointment: AppointmentDto;
  openTicketForm: (appointment: AppointmentDto) => void;
  patientId: number;
}

interface TimeSlotAttendanceDialogProps {
  appointment: AppointmentDto;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  isCalendarView: boolean;
  patientId: number;
  useShortCircut?: boolean;
}

export const TimeSlotAttendanceDialog = observer(
  ({
    appointment,
    dialogOpen,
    setDialogOpen,
    isCalendarView,
    patientId,
    useShortCircut = true,
  }: TimeSlotAttendanceDialogProps) => {
    const { mutate: setAttended } = useSetAttended();
    const { mutate: attendTimeSlot } = useAttendTimeSlot();
    const navigate = useNavigate();
    const patientsStore = useStore(PatientsContext);
    const { navigateToPatient } = PatientCommands(patientsStore);
    const [attendanceStatus, setAttendanceStatus] = useState<Record<string, boolean | null>>({});
    const [shouldShortCircuit, setShouldShortCircuit] = useState(false);

    // MD-89: when attending an appointment, surface all of its notes (Hinweise).
    // Notes are specific to this single appointment — never aggregated across the Rx.
    const { data: appointmentNotes = [], isLoading: notesLoading } = useAppointmentNotes(
      appointment?.id,
      dialogOpen
    );
    const hasNotes = appointmentNotes.length > 0;
    const isSingleSlot = appointment?.timeSlots?.length === 1;
    const sortedNotes = useMemo(
      () =>
        [...appointmentNotes].sort(
          (a, b) =>
            new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
        ),
      [appointmentNotes]
    );

    useEffect(() => {
      if (appointment?.timeSlots) {
        const initialStatus: Record<string, boolean | null> = {};
        appointment.timeSlots.forEach((slot: any) => {
          initialStatus[slot.id] = slot.attended ?? null;
        });
        setAttendanceStatus(initialStatus);
      }
    }, [appointment]);

    // When a single-slot appointment shows the dialog because it carries notes,
    // pre-select (mark attended) that single slot so the user only has to confirm.
    // Applied once per dialog open, after notes have loaded, and only when the
    // slot isn't already attended.
    const noteDefaultAppliedRef = useRef(false);
    useEffect(() => {
      if (!dialogOpen) {
        noteDefaultAppliedRef.current = false;
        return;
      }
      if (!noteDefaultAppliedRef.current && isSingleSlot && hasNotes && !notesLoading) {
        const singleSlot = appointment.timeSlots?.[0];
        if (singleSlot?.id != null) {
          setAttendanceStatus((prev) => ({ ...prev, [singleSlot.id!]: true }));
          noteDefaultAppliedRef.current = true;
        }
      }
    }, [dialogOpen, isSingleSlot, hasNotes, notesLoading, appointment]);

    // Handle short circuit logic in useEffect to avoid navigation during render
    useEffect(() => {
      if (
        shouldShortCircuit &&
        useShortCircut &&
        dialogOpen &&
        isSingleSlot &&
        !notesLoading &&
        !hasNotes
      ) {
        const singleSlot = appointment.timeSlots![0];
        const currentAttendanceStatus = singleSlot.attended === true;
        const newAttendanceStatus = !currentAttendanceStatus;
        
        // Create the new status object directly and pass it to handleSubmitAttendance
        const newStatus = {
          ...attendanceStatus,
          [singleSlot.id!]: newAttendanceStatus,
        };
        
        // Call handleSubmitAttendance with the new status directly to avoid race condition
        handleSubmitAttendance(newStatus);
        setShouldShortCircuit(false); // Reset the flag
      }
    }, [shouldShortCircuit, useShortCircut, dialogOpen, appointment, attendanceStatus, isSingleSlot, notesLoading, hasNotes]);    

    const handleStatusChange = (slotId: string, isAttended: boolean) => {
      setAttendanceStatus((prev) => ({
        ...prev,
        [slotId]: isAttended,
      }));
    };

    const handleDialogClose = () => {
      setDialogOpen(false);
    };

    /**
     * 1. Uses mutations to save the attendance status of time slots.
     * 2. Navigates to patient view after submission if in calendar view.
     * 3. Closes the dialog
     * @param overrideStatus - Optional parameter to override the current state for immediate submission
     */
    const handleSubmitAttendance = async (overrideStatus?: Record<string, boolean | null>) => {
      try {
        const statusToUse = overrideStatus || attendanceStatus;
        const valuesSet = new Set(Object.values(statusToUse));
        // console.log("Submitting attendance with status:", statusToUse);
        if (valuesSet.size === 1 && appointment?.id) {
          // All values are the same (either all true or all false)
          const [uniqueValue] = valuesSet;
          setAttended({
            appointmentId: appointment.id,
            attended: uniqueValue as boolean,
            patientId,
            appointmentStart: new Date(appointment?.start!),
          });
        } else {
          // Mixed values, update individually
          for (const [slotId, isAttended] of Object.entries(statusToUse)) {
            if (isAttended !== null) {
              attendTimeSlot({
                slotId,
                attended: isAttended,
                patientId,
                appointmentStart: new Date(appointment?.start!),
              });
            }
          }
        }
      } catch (error) {
        console.error("Error updating attendance:", error);
      } finally {
        const patientId = (appointment?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto).id;
        const rxId = appointment?.timeSlots?.[0]?.therapyRx?.rx?.id;

        // console.debug("Navigating to patient view after attendance submission", patientId, rxId, isCalendarView);
        if (patientId && rxId && isCalendarView) {
          navigateToPatient(navigate, patientId, rxId, PATIENT_SUBTABS.PRESCRIPTIONS);
        }
        setDialogOpen(false);
      }
    };

    const formatTime = (timeStr: string) => {
      const date = new Date(timeStr);
      return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
    };

    // Handle button click - wrapper function
    const handleButtonSubmit = () => {
      handleSubmitAttendance();
    };

    // Handle short circuit for single slot appointments.
    // MD-89: only auto-submit (no dialog) when the appointment has NO notes.
    // While notes are still loading we defer the decision to avoid flashing the
    // dialog; when notes exist we always show the dialog so they are seen.
    if (useShortCircut && dialogOpen && isSingleSlot && !hasNotes) {
      if (!notesLoading && !shouldShortCircuit) {
        // Trigger the short circuit logic in useEffect
        setShouldShortCircuit(true);
      }
      return null; // Prevent rendering the dialog
    }

    return (
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Anwesenheit speichern</DialogTitle>
        <DialogContent>
          {hasNotes && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 0.5 }}
              >
                Hinweise zum Termin
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${mobiliTheme.mainHeader}`,
                  borderRadius: "0.4em",
                  backgroundColor: mobiliTheme.lowLevelContainerBackground,
                  px: 1,
                  py: 0.5,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {sortedNotes.map((note, index) => (
                  <Box
                    key={note.id || index}
                    sx={{ display: "flex", gap: 1.5, py: 1 }}
                  >
                    <MobiliAvatar
                      user={note.updatedByUser || note.createdByUser}
                      sx={{ width: 32, height: 32, fontSize: "0.8rem", mt: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {note.createdAt
                          ? `${formatDateUI(new Date(note.createdAt))} ${formatTime(note.createdAt)}`
                          : "Jetzt"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {note.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              position: "relative",
              border: `1px solid ${mobiliTheme.mainHeader}`,
              p: 0,
              m: 0,
              padding: "0px",
              paddingBottom: "4px",
              margin: "0px",
              backgroundColor: mobiliTheme.lowLevelContainerBackground,
              borderRadius: "0.4em",
            }}
          >
            {/* Same order the card that opened this dialog shows (MD-411). These
                slots are always scheduled — attendance is recorded after the
                fact — so this is the chronological branch, and the rows carry
                their own times. Reading the order off the helper rather than
                off however the API happened to return them is what keeps the
                two lists agreeing. */}
            {slotsInChronologicalOrder(appointment?.timeSlots ?? []).map((timeSlot: any, index: number) => (
              <TherapySlotRow
                key={timeSlot.id ?? index}
                timeSlot={timeSlot}
                appointment={appointment}
                index={index}
                totalSlots={appointment?.timeSlots?.length || 0}
                displayTimeslotTime
                trailing={
                  <IconButton
                    onClick={() =>
                      handleStatusChange(timeSlot.id, !(attendanceStatus[timeSlot.id] === true))
                    }
                    sx={{ padding: 0 }}
                  >
                    {attendanceStatus[timeSlot.id] === true ? (
                      <CheckCircle
                        sx={{ width: 20, height: 20, color: mobiliTheme.palette.success.main }}
                      />
                    ) : (
                      <CircleOutlined sx={{ width: 20, height: 20 }} />
                    )}
                  </IconButton>
                }
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Abbrechen</Button>
          <Button onClick={handleButtonSubmit} color="primary" variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const AppointmentMenu = observer(
  ({
    appointment,
    handleOpenCancellationDialog,
    openTicketForm,
    patientId,
    handleCopy,
    onAddNotes,
    isView,
  }: MenuInterface) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false); // State for the edit dialog
    const AppointmentStore = useStore(AppointmentContext);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [informedDialogOpen, setInformedDialogOpen] = useState(false);
    const { warningDialogMessage, setWarningDialogMessage } = AppointmentStore;

    const handleOpenInformedDialog = () => {
      setInformedDialogOpen(true);
      handleMenuClose();
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    // const handleAttended = async (appointmentId: any, isAttended: boolean) => {
    //   try {
    //     // Call your API or setAttended function with the appointmentId and isAttended value
    //     await setAttended(appointmentId, isAttended);
    //     // setDataRefreshFlag(isAttended);
    //     setDataRefreshFlag(!dataRefreshFlag);
    //   } catch (error) {
    //     console.error("Error marking as attended/unattended:", error);
    //   }
    // };

    const handleDialogOpen = () => {
      // Initialize with current values if needed
      const initialStatus: Record<string, boolean | null> = {};
      appointment.timeSlots?.forEach((slot: any) => {
        initialStatus[slot.id] = slot.attended ?? null;
      });
      setDialogOpen(true);
      handleMenuClose();
    };

    // Modern calendar navigation using projection system
    const navigate = useNavigate();
    const calendarStore = useStore(CalendarContext);
    const { navigateToCalendar } = CalendarCommands(calendarStore);
    const handleJumpToCalendar = (appointment: AppointmentDto) => {
      const patientData = appointment?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto;
      // console.debug("Patient Data for jumping to calendar:", patientData, appointment.start);
      navigateToCalendar(navigate, {
        patientId,
        patientLastName: patientData?.lastName,
        date: moment(appointment.start).format("YYYY-MM-DD"),
        appointmentId: appointment.id,
      });
      handleMenuClose();
    };

    const handleEditDialogOpen = () => {
      setEditDialogOpen(true);
      handleMenuClose();
    };

    const handleEditDialogClose = () => {
      setEditDialogOpen(false);
      handleMenuClose();
    };

    const handleCloseWarning = () => {
      setWarningDialogMessage("");
    };

    const currentDate: any = new Date();
    return (
      <>
        {warningDialogMessage !== "" && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseWarning} // Function to close the dialog
            title="Bearbeitungsstatus aktualisiert"
            content={warningDialogMessage}
            typeToProceed={["Ok"]}
          />
        )}
        <IconButton
          sx={denseMenuButtonSx}
          aria-label="more"
          aria-controls={`menu-${appointment.id}`}
          aria-haspopup="true"
          onClick={(event) => handleMenuOpen(event)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          id={`menu-${appointment.id}`}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleJumpToCalendar(appointment)}>
            <Today />
            zum Kalender
          </MenuItem>
          <MenuItem onClick={handleEditDialogOpen}>
            <Edit />
            Bearbeiten
          </MenuItem>
          {!appointment.attended && appointment.start && appointment.end && (
            <MenuItem onClick={() => handleOpenCancellationDialog(appointment.id)}>
              <Cancel className="cancel-icon" />
              stornieren
            </MenuItem>
          )}
          {appointment.start &&
            new Date(new Date(appointment.start).getTime() - 20 * 60 * 1000) < currentDate && (
              <MenuItem onClick={handleDialogOpen}>
                <CheckCircle />
                Anwesenheit ändern
              </MenuItem>
          )}
          <MenuItem onClick={handleOpenInformedDialog}>
            <RecordVoiceOver />
            Patient informieren
          </MenuItem>
          <MenuItem
            onClick={() => {
              openTicketForm(appointment);
              handleMenuClose();
            }}
          >
            <Note /> Ticket erstellen
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCopy(appointment.id!, false); // flag set to true for continuous appointments
              handleMenuClose();
            }}
          >
            <Add /> Neuer Termin
          </MenuItem>
          <MenuItem
            onClick={(event: any) => {
              onAddNotes(event);
              handleMenuClose();
            }}
          >
            <Edit />
            Notizen hinzufügen/bearbeiten
          </MenuItem>
        </Menu>
        {editDialogOpen && (
          <AppointmentEditCard
            open={editDialogOpen}
            onClose={handleEditDialogClose}
            appointment={appointment}
            patientId={patientId}
            isView={isView}
          />
        )}
        {dialogOpen && (
          <TimeSlotAttendanceDialog
            appointment={appointment}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            isCalendarView={false}
            patientId={patientId}
          />
        )}
        <SetInformedDialog
          open={informedDialogOpen}
          onClose={() => setInformedDialogOpen(false)}
          appointmentId={appointment?.id}
          appointmentStart={appointment?.start ? new Date(appointment.start) : undefined}
          projection={appointment?.informing}
          visitType={appointment?.address?.type}
          patientName={getAppointmentPatientName(appointment)}
          slots={getAppointmentSlots(appointment)}
        />
      </>
    );
  }
);
const CancelledAppointmentMenu = observer(
  ({ appointment, openTicketForm, patientId }: CancelledMenuInterface) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { mutate: cancellationOfCancelledAppointment } = useCancellationOfCancelledAppointment();
    const [loading, setLoading] = useState(false); // State to manage loading indicator
    

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    const cancellationOfCancelledAppointmentOnClick = async () => {
      setLoading(true);
      try {
        const response = cancellationOfCancelledAppointment({
          appointmentId: (appointment as any).cancelledAppointmentId,
          resolved: true,
          patientId,
        });
      } catch (error) {
        // Handle error if needed
        console.error("Error cancelling appointment:", error);
      } finally {
        setLoading(false);
        handleMenuClose();
      }
    };
    const [cancelReasonDialogOpen, setCancelReasonDialogOpen] = useState(false);

    const handleOpenCancelReasonDialog = (appointment: AppointmentDto) => {
      setCancelReasonDialogOpen(true);
    };

    const handleCloseCancelReasonDialog = () => {
      setCancelReasonDialogOpen(false);
    };





    return (
      <>
        <IconButton
          sx={denseMenuButtonSx}
          aria-label="more"
          aria-controls={`menu-${appointment.id}`}
          aria-haspopup="true"
          onClick={(event) => handleMenuOpen(event)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          id={`menu-${appointment.id}`}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              cancellationOfCancelledAppointmentOnClick();
            }}
          >
            <Tooltip title="Ausfallen lassen">
              <div style={{ display: "flex", alignItems: "center" }}>
                {loading ? (
                  <CircularProgress size={20} sx={{ marginRight: 1, color: "black" }} /> // Show loading spinner when loading is true
                ) : (
                  <Cancel className="cancel-icon" />
                )}
                Ausfallen lassen
              </div>
            </Tooltip>
          </MenuItem>
          <MenuItem
            onClick={() => {
              openTicketForm(appointment);
              handleMenuClose();
            }}
          >
            <Note /> Ticket erstellen
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleOpenCancelReasonDialog(appointment);
              handleMenuClose();
            }}
          >
            <Edit /> Stornierungsgrund bearbeiten
          </MenuItem>
        </Menu>
        {cancelReasonDialogOpen && (
          <EditCancellationDialog
            open={cancelReasonDialogOpen}
            onClose={handleCloseCancelReasonDialog}
            appointment={appointment}
            patientId={patientId}
          />
        )}
      </>
    );
  }
);

const ContinuousAppointmentMenu = ({
  appointment,
  patientId,
  handleCopy,
  onAddNotes,
}: MenuInterface) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const AppointmentStore = useStore(AppointmentContext);
  const { mutateAsync: backfillContinuousAppointment } = useBackfillContinuousAppointment();
  const { mutateAsync: cancelContinuousAppointment } = useCancelContinuous();
  const [editDialogOpen, setEditDialogOpen] = useState(false); // State for the edit dialog

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBackfill = async (appointment: AppointmentDto) => {
    try {
      // Call your API or backfill function with the appointment object
      // console.log("Backfilling appointment:", appointment);
      if (!appointment.id) {
        notificationStore.showMessage(
          "No appointment ID found for backfilling appointment:",
          "error"
        );
        return;
      }
      const response: any = await backfillContinuousAppointment({ appointmentId: appointment.id });
      if (!response) return;

      if (response.backfilled === 0) {
        notificationStore.showMessage("Keine termine notwendig", "warning");
        return;
      } else {
        notificationStore.showMessage("" + response.backfilled + " termine erzeugt", "success");
        // setDataRefreshFlag(appointment);
      }
    } catch (error) {
      console.error("Error backfilling appointment:", error);
    }
  };

  const handleCancel = async (appointment: AppointmentDto) => {
    try {
      // Call your API or backfill function with the appointment object
      // console.log("Cancelling appointment:", appointment);
      if (!appointment.id) {
        notificationStore.showMessage(
          "No appointment ID found for cancelling appointment:",
          "error"
        );
        return;
      }
      const response: any = await cancelContinuousAppointment({ appointmentId: appointment.id });
      if (response) notificationStore.showMessage("Termin storniert", "success");
      handleMenuClose();
    } catch (error) {
      console.error("Error backfilling appointment:", error);
    }
  };

  const handleEditDialogOpen = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    handleMenuClose();
  };

  // Modern calendar navigation for continuous appointments
  const navigate = useNavigate();
  const calendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(calendarStore);
  const handleJumpToCalendar = (appointment: AppointmentDto) => {
    const patientData = appointment?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto;
    const patientIdLocal = patientData?.id;
    navigateToCalendar(navigate, {
      patientId: patientIdLocal,
      patientLastName: patientData?.lastName,
      date: moment(appointment.start).format("YYYY-MM-DD"),
      appointmentId: appointment.id,
    });
  };

  return (
    <>
      <IconButton
        sx={denseMenuButtonSx}
        aria-label="more"
        aria-controls={`menu-${appointment.id}`}
        aria-haspopup="true"
        onClick={(event) => handleMenuOpen(event)}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        id={`menu-${appointment.id}`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleJumpToCalendar(appointment)}>
          <Today />
          zum Kalender
        </MenuItem>
        <MenuItem onClick={handleEditDialogOpen}>
          <Edit />
          Bearbeiten
        </MenuItem>
        <MenuItem onClick={() => handleCancel(appointment)}>
          <Cancel className="cancel-icon" />
          stornieren
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleBackfill(appointment);
            handleMenuClose();
          }}
        >
          <ManageHistory /> Züruckplanen
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCopy(appointment.id!, true); // flag set to true for continuous appointments
            handleMenuClose();
          }}
        >
          <Add /> Neuer Termin
        </MenuItem>
        {/* <MenuItem onClick={onAddNotes}
          <Edit />
          Notizen hinzufügen/bearbeiten
        </MenuItem> */}
      </Menu>
      {editDialogOpen && (
        <AppointmentEditCard
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          appointment={appointment}
          patientId={patientId}
          isContinous={true}
        />
      )}
    </>
  );
};

export default TerminView;
